import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { sendOtpCode, verifyOtpCode, resetPassword } from '../api/auth';
import Button from '../components/Form/Button';
import TextField from '../components/Form/TextField';

const ForgetPasswordScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const [step, setStep] = useState('otp');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState('');

  useEffect(() => {
    if (!email) {
      navigate('/signin');
    } else {
      const otpSent = localStorage.getItem(`otp_sent_for_${email}`);
      if (!otpSent) {
        sendOtp();
      } else {
        // set cooldown if less than 30s
        const lastSent = parseInt(otpSent, 10);
        const secondsSinceLastSent = Math.floor((Date.now() - lastSent) / 1000);
        if (secondsSinceLastSent < 30) {
          setResendCooldown(30 - secondsSinceLastSent);
        }
      }
    }
  }, [email, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const sendOtp = async () => {
    try {
      await sendOtpCode(email);
      swal("Succès!", "Code OTP envoyé à votre adresse e-mail.", "success");
      localStorage.setItem(`otp_sent_for_${email}`, Date.now().toString());
      setResendCooldown(30);
    } catch (err) {
      swal("Erreur!", err.response?.data?.message || "Erreur lors de l'envoi du code OTP.", "error");
      navigate('/signin');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await sendOtpCode(email);
      swal("Succès!", "Nouveau code OTP envoyé.", "success");
      localStorage.setItem(`otp_sent_for_${email}`, Date.now().toString());
      setResendCooldown(30);
    } catch (err) {
      swal("Erreur!", err.response?.data?.message || "Erreur lors du renvoi du code OTP.", "error");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      swal("Erreur!", "Veuillez entrer le code OTP.", "error");
      return;
    }

    try {
      await verifyOtpCode(email, otp);
      swal("Succès!", "Code OTP vérifié avec succès.", "success");
      setStep('reset');
    } catch (err) {
      swal("Erreur!", err.response?.data?.message || "Code OTP invalide.", "error");
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length < 8) return 'Faible';
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    if (hasLower && hasNumber && !hasUpper && !hasSpecial) return 'Faible';
    if (hasLower && hasUpper && hasNumber && !hasSpecial) return 'Moyen';
    if (hasLower && hasUpper && hasNumber && hasSpecial) return 'Fort';
    return 'Faible';
  };

  const getStrengthPercent = (strength) => {
    switch (strength) {
      case 'Faible':
        return 30;
      case 'Moyen':
        return 60;
      case 'Fort':
        return 100;
      default:
        return 0;
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!newPassword) {
      newErrors.newPassword = "Le mot de passe est requis.";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Le mot de passe doit contenir au moins 8 caractères.";
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "La confirmation du mot de passe est requise.";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validatePassword()) return;

    try {
      await resetPassword(email, newPassword);
      swal("Succès!", "Mot de passe réinitialisé avec succès.", "success").then(() => {
        localStorage.removeItem(`otp_sent_for_${email}`);
        navigate('/signin');
      });
    } catch (err) {
      swal("Erreur!", err.response?.data?.message || "Erreur lors de la réinitialisation du mot de passe.", "error");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordStrength(getPasswordStrength(value));
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'Faible':
        return 'bg-red-500';
      case 'Moyen':
        return 'bg-yellow-500';
      case 'Fort':
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-paragaiaLight p-6">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
        <h2 className="text-2xl font-bold mb-6 text-center text-paragaia">Réinitialiser le mot de passe</h2>

        {step === 'otp' && (
          <>
            <TextField
              type="text"
              placeholder="Entrez votre code OTP"
              value={otp}
              name="otp"
              required
              onChange={(e) => setOtp(e.target.value)}
            />
            <Button
              text="Vérifier le code OTP"
              className="w-full mt-6 bg-paragaia text-white py-3 rounded-lg hover:bg-paragaiaLight transition poppins"
              onClick={handleVerifyOtp}
            />
            <button
              onClick={handleResendOtp}
              disabled={resendCooldown > 0}
              className={`text-sm text-center ${
                resendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:underline'
              } mt-4`}
            >
              {resendCooldown > 0
                ? `Renvoyer le code OTP (${resendCooldown}s)`
                : 'Renvoyer le code OTP'}
            </button>
          </>
        )}

        {step === 'reset' && (
          <>
            <div className="flex flex-col space-y-4">
              <div>
                <TextField
                  type="password"
                  placeholder="Nouveau mot de passe"
                  value={newPassword}
                  name="newPassword"
                  required
                  onChange={handlePasswordChange}
                />
                <div className="w-full bg-gray-200 h-2 rounded-full mt-2">
                  <div
                    className={`h-2 rounded-full ${getStrengthColor(passwordStrength)}`}
                    style={{ width: `${getStrengthPercent(passwordStrength)}%` }}
                  />
                </div>
                {passwordStrength && (
                  <p className={`text-sm mt-1 ${getStrengthColor(passwordStrength).replace('bg', 'text')}`}>
                    Force du mot de passe: {passwordStrength}
                  </p>
                )}
                {errors.newPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
                )}
              </div>

              <div>
                <TextField
                  type="password"
                  placeholder="Confirmer mot de passe"
                  value={confirmPassword}
                  name="confirmPassword"
                  required
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <Button
              text="Réinitialiser le mot de passe"
              className="w-full mt-6 bg-paragaia text-white py-3 rounded-lg hover:bg-paragaiaLight transition poppins"
              onClick={handleResetPassword}
            />
          </>
        )}
      </div>
    </main>
  );
};

export default ForgetPasswordScreen;
